import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  ErrorMsg,
  Incoming,
  LeaderRow,
  MeMsg,
  ParticipantRow,
  ParticipantsMsg,
  ResultsMsg,
  StateMsg,
  WelcomeMsg,
} from './types'

export type Role = 'play' | 'present' | 'admin'

/** Сколько отложенных команд храним: больше — уже не «нажал до подключения», а мусор. */
const OUTBOX_LIMIT = 20

/**
 * Сторож соединения.
 *
 * Телефон в зале теряет сеть молча: вебсокет остаётся «открытым», событие
 * close не приходит, и участник минутами смотрит в замерший экран. Поэтому
 * шлём пустой ping и, если в ответ давно ничего не приходило, рвём соединение
 * сами — дальше сработает обычное переподключение.
 */
const PING_MS = 15_000
const SILENCE_LIMIT_MS = 40_000

interface LiveOptions {
  role: Role
  code: string
  /** Только для роли play. */
  participantId?: string | null
  enabled?: boolean
}

export interface Live {
  connected: boolean
  /**
   * Связь была и пропала — в отличие от «ещё не подключились».
   * Зал не должен видеть тревожную плашку на старте эфира, пока вебсокет
   * только открывается: это норма, а не авария.
   */
  reconnecting: boolean
  /** Есть ли команды, ожидающие открытия соединения. */
  pending: number
  state: StateMsg | null
  results: ResultsMsg | null
  me: MeMsg | null
  leaderboard: LeaderRow[]
  participants: ParticipantRow[]
  /** Сводка по залу для пульта: сколько всего, на связи и ответило. */
  roster: { count: number; connected: number; answered: number; shown: number }
  /**
   * Кто я в этом эфире — по версии сервера.
   *
   * Имя участника нельзя брать только из localStorage: при повторном заходе
   * вход по REST не выполняется, и без этого сообщения на экране навсегда
   * оставалось многоточие вместо имени.
   */
  welcome: WelcomeMsg | null
  error: ErrorMsg | null
  /**
   * Отправить команду. Если соединение ещё не открылось или переподключается,
   * команда встаёт в очередь и уйдёт сама — терять нажатия ведущего нельзя.
   */
  send: (msg: Record<string, unknown>) => void
  /** Подписка на дельты тапов — для анимации всплывающих эмодзи. */
  onTaps: (listener: (deltas: Record<string, number>) => void) => () => void
}

/**
 * Одно вебсокет-соединение на страницу, с переподключением.
 *
 * Дельты тапов идут не через useState, а через подписку: при живом эфире они
 * приходят несколько раз в секунду, и перерисовывать на каждую из них всё дерево
 * незачем — анимацией управляет только сам компонент с эмодзи.
 */
export function useLive({ role, code, participantId, enabled = true }: LiveOptions): Live {
  const [connected, setConnected] = useState(false)
  const [state, setState] = useState<StateMsg | null>(null)
  const [results, setResults] = useState<ResultsMsg | null>(null)
  const [me, setMe] = useState<MeMsg | null>(null)
  const [leaderboard, setLeaderboard] = useState<LeaderRow[]>([])
  const [participants, setParticipants] = useState<ParticipantRow[]>([])
  const [roster, setRoster] = useState({ count: 0, connected: 0, answered: 0, shown: 0 })
  const [error, setError] = useState<ErrorMsg | null>(null)
  const [welcome, setWelcome] = useState<WelcomeMsg | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const tapListeners = useRef(new Set<(d: Record<string, number>) => void>())
  const retry = useRef(0)
  const closedByUs = useRef(false)
  const lastSeen = useRef(Date.now())
  const outbox = useRef<Record<string, unknown>[]>([])
  const [pending, setPending] = useState(0)
  const [everConnected, setEverConnected] = useState(false)

  const ready = enabled && !!code && (role !== 'play' || !!participantId)

  useEffect(() => {
    if (!ready) return
    closedByUs.current = false
    let reconnectTimer: number | undefined

    const open = () => {
      const scheme = location.protocol === 'https:' ? 'wss' : 'ws'
      const query = new URLSearchParams({ code })
      if (role === 'play' && participantId) query.set('pid', participantId)
      const ws = new WebSocket(`${scheme}://${location.host}/ws/${role}?${query}`)
      wsRef.current = ws

      ws.onopen = () => {
        retry.current = 0
        lastSeen.current = Date.now()
        setConnected(true)
        setEverConnected(true)
        // Всё, что нажали до открытия соединения, уходит сейчас и в том же порядке.
        const queued = outbox.current
        outbox.current = []
        setPending(0)
        queued.forEach((msg) => ws.send(JSON.stringify(msg)))
      }

      ws.onmessage = (event) => {
        lastSeen.current = Date.now()
        const msg = JSON.parse(event.data) as Incoming
        switch (msg.t) {
          case 'state':
            setState(msg)
            break
          case 'results':
            setResults(msg)
            break
          case 'me':
            setMe(msg)
            break
          case 'leaderboard':
            setLeaderboard(msg.rows)
            break
          case 'participants': {
            const people = msg as ParticipantsMsg
            setParticipants(people.rows)
            setRoster({
              count: people.count,
              connected: people.connected,
              answered: people.answered,
              shown: people.shown,
            })
            break
          }
          case 'taps':
            tapListeners.current.forEach((listener) => listener(msg.deltas))
            break
          case 'error':
            setError(msg)
            break
          case 'welcome':
            setWelcome(msg)
            break
        }
      }

      ws.onclose = () => {
        setConnected(false)
        if (closedByUs.current) return
        // Зал не должен ничего перезагружать руками: возвращаемся сами,
        // наращивая паузу, чтобы не долбить упавший сервер.
        const delay = Math.min(1000 * 2 ** retry.current, 10_000)
        retry.current += 1
        reconnectTimer = window.setTimeout(open, delay)
      }
    }

    open()

    const watchdog = window.setInterval(() => {
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) return

      if (Date.now() - lastSeen.current > SILENCE_LIMIT_MS) {
        // Соединение мертво, хотя браузер считает иначе: закрываем сами,
        // чтобы сработало переподключение и участник увидел предупреждение.
        ws.close()
        return
      }
      ws.send(JSON.stringify({ t: 'ping' }))
    }, PING_MS)

    return () => {
      closedByUs.current = true
      window.clearInterval(watchdog)
      window.clearTimeout(reconnectTimer)
      wsRef.current?.close()
      wsRef.current = null
    }
  }, [ready, role, code, participantId])

  const send = useCallback((msg: Record<string, unknown>) => {
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg))
      return
    }
    // Тапы копить бессмысленно: их ценность в моменте, и к открытию соединения
    // они уже неактуальны. Всё остальное — команды, их терять нельзя.
    if (msg.t === 'tap') return
    outbox.current = [...outbox.current.slice(-OUTBOX_LIMIT), msg]
    setPending(outbox.current.length)
  }, [])

  const onTaps = useCallback((listener: (deltas: Record<string, number>) => void) => {
    tapListeners.current.add(listener)
    return () => {
      tapListeners.current.delete(listener)
    }
  }, [])

  // Ошибку показываем недолго — она про конкретное действие, а не про состояние.
  useEffect(() => {
    if (!error) return
    const timer = window.setTimeout(() => setError(null), 3500)
    return () => window.clearTimeout(timer)
  }, [error])

  return {
    connected,
    reconnecting: everConnected && !connected,
    pending,
    state,
    results,
    me,
    leaderboard,
    participants,
    roster,
    welcome,
    error,
    send,
    onTaps,
  }
}

/**
 * Локальный отсчёт таймера. Сервер присылает остаток один раз при открытии слайда,
 * дальше тикаем сами — так и трафика нет, и цифра не дёргается.
 */
export function useCountdown(msLeft: number | null | undefined): number | null {
  const [left, setLeft] = useState<number | null>(msLeft ?? null)

  useEffect(() => {
    if (msLeft == null) {
      setLeft(null)
      return
    }
    const deadline = Date.now() + msLeft
    setLeft(msLeft)
    const timer = window.setInterval(() => {
      const remaining = deadline - Date.now()
      setLeft(remaining > 0 ? remaining : 0)
      if (remaining <= 0) window.clearInterval(timer)
    }, 200)
    return () => window.clearInterval(timer)
  }, [msLeft])

  return left
}
